import { useState, Fragment, useEffect } from 'react'
import { useRouter } from 'next/router'
import Skeleton from 'react-loading-skeleton'
import {
  motion,
  AnimatePresence,
  MotionValue,
  useMotionTemplate
} from 'framer-motion'
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik'

import { useCart } from '@hooks/price'
import { Input } from '@components/UI/Form/Input'
import { Button } from '@components/UI/Button'
import { Tooltip } from '@components/UI/Tooltip'
import { InvalidMessage } from '@components/UI/Form/InvalidMessage'
import { createClient } from '@services/api'
import { Cart as CartType } from '@hooks/price/types'
import { FormValues } from '.'
import toast from 'react-hot-toast'

interface CartProps {
  isSubmitting: boolean
  progress: MotionValue
  progressMessage: string
  submitOrder: () => void | Promise<void>
  setPromoCode: (code: string) => void
  showPayment: () => void
  hidePayment: () => void
}

export function Cart(props: CartProps) {
  const { query } = useRouter()
  const [promoCode, setPromoCode] = useState('')
  const progress = useMotionTemplate`${props.progress}%`
  const cart = useCart(promoCode)
  const { errors, validateForm, submitForm } = useFormikContext<FormValues>()

  useEffect(() => {
    void validateForm()
    if (cart.status === 'success') {
      if (cart.data.total?.raw === 0) {
        props.hidePayment()
      } else {
        props.showPayment()
      }
    }
  }, [cart.status])

  async function verifyPromoCode(values: { promoCode: string }) {
    if (values.promoCode.trim()) {
      try {
        const { data } = await createClient().get<CartType>('/cart', {
          params: {
            promoCode: values.promoCode.trim().toUpperCase(),
            partner:
              query.ref?.toString().toLowerCase() !== 'inspectcheck'
                ? query.ref
                : '',
            affiliate: query.affiliate
          }
        })

        if (data.total.raw === 0) props.hidePayment()

        const discount = data.discounts.find(discount => discount.code)

        if (discount) {
          props.setPromoCode(discount.code as string)
        }

        void handleVoucherBlur(values.promoCode)
      } catch (error) {
        if (error.response.status === 404) {
          return {
            promoCode: "Sorry, we don't recognize that code"
          }
        }
      }
    }
  }

  async function applyPromoCode(values: { promoCode: string }) {
    setPromoCode(values.promoCode)
  }

  async function handleVoucherBlur(promoCode: string) {
    if (promoCode.trim().length) {
      void applyPromoCode({ promoCode })
    }
  }

  function handleSubmit() {
    const keys = ['inspection', 'property', 'signup', 'billing']

    const elements = keys.map(key => {
      if (errors[key] && Object.keys(errors[key]).length) {
        return document.getElementById(key)
      }
    })

    const scrollElement = elements.find(element => element !== undefined)
    if (scrollElement) {
      scrollElement.scrollIntoView({ behavior: 'smooth' })
    }

    if (
      !Object.keys(errors).length ||
      (Object.keys(errors).length &&
        errors.billing &&
        !Object.keys(errors.billing).length)
    ) {
      void submitForm()
    } else {
      toast.error('Please complete all required fields')
      void validateForm()
      void submitForm()
    }
  }

  return (
    <div className="md:sticky md:top-0 lg:pt-8">
      <div className="bg-blue-100 p-8 rounded shadow-sm">
        <h3 className="text-2xl font-black tracking-tight text-brand">
          Shopping Cart
        </h3>
        <div className="h-px w-full bg-blue-200 my-8" />
        {cart.data?.products.length ? (
          <div className="flex justify-between">
            {cart.data?.products?.map(product => (
              <Fragment key={product.price.id}>
                <div className="w-32 h-24 bg-brand folded-r uppercase text-white flex justify-center items-center text-xs tracking-wider font-black">
                  Domoreport
                </div>
                <div className="flex flex-col justify-between items-end">
                  <h4 className="text-blue-900 font-bold text-right">
                    {product.name}
                  </h4>
                  <span className="font-normal">x1</span>
                  <p
                    className={
                      cart.data?.discounts.length ? 'line-through' : ''
                    }
                  >
                    {product.price.formatted}
                  </p>
                </div>
              </Fragment>
            ))}
          </div>
        ) : (
          <div className="flex justify-between">
            <Fragment>
              <div className="w-32 h-24 bg-brand folded-r uppercase text-white flex justify-center items-center text-xs tracking-wider font-black">
                Domoreport
              </div>
              <div className="flex flex-col justify-between items-end">
                <Skeleton width={200} height={20} />
                <Skeleton width={100} height={20} />
                <Skeleton width={100} height={20} />
              </div>
            </Fragment>
          </div>
        )}
        {cart.data?.discounts?.map(discount => (
          <Fragment key={discount.amount.id}>
            <div className="h-px w-full bg-blue-200 my-8" />
            <div className="flex justify-between">
              <div className="flex item-center">
                {discount.code ?? discount.name}
                {discount.code && (
                  <button
                    className="ml-2"
                    type="button"
                    data-testid="removeVoucherButton"
                    onClick={() => {
                      setPromoCode('')
                      props.showPayment()
                    }}
                  >
                    <Tooltip label="Remove Voucher">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        width={16}
                        height={16}
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Tooltip>
                  </button>
                )}
              </div>
              <div className="text-red-500">-{discount.amount.formatted}</div>
            </div>
          </Fragment>
        ))}
      </div>
      {!cart.data?.discounts?.find(discount => discount.code) &&
        cart.data?.total.raw !== 0 && (
          <Formik
            initialValues={{ promoCode: '' }}
            validate={verifyPromoCode}
            validateOnChange={false}
            validateOnBlur
            onSubmit={applyPromoCode}
          >
            {formik => (
              <Form className="bg-white rounded-b border-gray-100 p-8 shadow-sm">
                <div className="flex">
                  <Field
                    component={Input}
                    name="promoCode"
                    placeholder="ENTER VOUCHER"
                    className="uppercase"
                    showErrors={false}
                  />
                  <Tooltip label="Apply Voucher">
                    <button
                      disabled={!formik.values.promoCode}
                      className="rounded ml-2 uppercase text-sm p-4 bg-blue-100 text-blue-900 transition-all hover:bg-blue-200 disabled:bg-gray-300 disabled:text-gray-600 disabled:opacity-50 disabled:cursor-default"
                    >
                      Apply
                    </button>
                  </Tooltip>
                </div>
                <ErrorMessage component={InvalidMessage} name="promoCode" />
              </Form>
            )}
          </Formik>
        )}
      <AnimatePresence exitBeforeEnter>
        {props.isSubmitting ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-4 relative w-full h-16 bg-green-100 shadow-sm rounded flex items-center justify-center"
          >
            <span className="font-black text-green-700 tracking-wider uppercase z-10">
              {props.progressMessage}
            </span>
            <motion.div
              className="absolute top-0 left-0 h-full rounded animate-pulse bg-green-300"
              style={{ width: progress }}
            />
          </motion.div>
        ) : (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-4 bg-white p-8 shadow-sm rounded flex sm:flex-row flex-col justify-between"
          >
            <Button onClick={handleSubmit}>Place Order</Button>
            <div className="flex flex-col justify-between items-end sm:order-last order-first sm:mb-0 mb-4">
              <p className="text-gray-500 font-bold tracking-wide text-xs uppercase">
                Total
              </p>
              <p className="font-black text-2xl">
                {cart.data?.total.formatted ?? (
                  <Skeleton height={24} width={80} />
                )}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
